import React, { useState, useEffect, useRef } from "react";

import { Link } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { useAlert } from "react-alert";

import {
  forgotPassword,
  verifyPasswordResetOtp,
  resendPasswordResetOtp,
  resetPassword,
  clearErrors,
} from "./../../actions/authActions";
import { FORGOT_PASSWORD_RESET } from "../../constants/authConstants";
import AuthCommonBanner from "./AuthCommonBanner";

const STEPS = { EMAIL: "email", OTP: "otp", NEW_PASSWORD: "newPassword" };

const ForgotPassword = ({ history }) => {
  const alert = useAlert();
  const dispatch = useDispatch();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorData, setErrorData] = useState({});
  const [countdown, setCountdown] = useState(0);

  const otpRefs = useRef([]);

  const {
    error,
    loading,
    message,
    passwordErrors,
    otpSent,
    otpVerified,
    resetToken,
    success,
    resending,
    resendMessage,
    resendError,
  } = useSelector((state) => state.forgotPassword);

  // Handle errors
  useEffect(() => {
    if (passwordErrors) {
      setErrorData(passwordErrors);
    }
    if (error) {
      alert.error(error);
      dispatch(clearErrors());
    }
    if (resendError) {
      alert.error(resendError);
    }
    if (resendMessage) {
      alert.success(resendMessage);
    }
  }, [dispatch, alert, error, passwordErrors, resendError, resendMessage]);

  // Move to OTP step when OTP is sent
  useEffect(() => {
    if (otpSent && step === STEPS.EMAIL) {
      alert.success(message || "OTP sent to your email");
      setStep(STEPS.OTP);
      setCountdown(60);
    }
  }, [otpSent, step, message, alert]);

  // Move to new password step when OTP is verified
  useEffect(() => {
    if (otpVerified && resetToken && step === STEPS.OTP) {
      alert.success("OTP verified successfully");
      setStep(STEPS.NEW_PASSWORD);
    }
  }, [otpVerified, resetToken, step, alert]);

  // Handle successful password reset
  useEffect(() => {
    if (success) {
      alert.success("Password updated successfully");
      dispatch({ type: FORGOT_PASSWORD_RESET });
      history.push("/login");
    }
  }, [success, history, alert, dispatch]);

  // Resend countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch({ type: FORGOT_PASSWORD_RESET });
    };
  }, [dispatch]);

  // --- Step 1: Submit email ---
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    dispatch(forgotPassword(email));
  };

  // --- Step 2: OTP input handlers ---
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtp[i] = pastedData[i];
      }
      setOtp(newOtp);
      const nextIndex = Math.min(pastedData.length, 5);
      otpRefs.current[nextIndex]?.focus();
    }
  };

  const handleOtpSubmit = (e) => {
    e.preventDefault();
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      alert.error("Please enter the complete 6-digit OTP");
      return;
    }
    dispatch(verifyPasswordResetOtp(email, otpString));
  };

  const handleResendOtp = () => {
    dispatch(resendPasswordResetOtp(email));
    setCountdown(60);
  };

  // --- Step 3: New password ---
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert.error("Passwords do not match");
      return;
    }
    dispatch(resetPassword(resetToken, password, confirmPassword));
  };

  return (
    <section className="registration-page clearfix fullHt">
      <div className="right-regi-step login-page">
        <div className="vh-center">
          <h1>
            Forgot <span>Password</span>
          </h1>

          {/* Step indicator */}
          <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "20px" }}>
            {[STEPS.EMAIL, STEPS.OTP, STEPS.NEW_PASSWORD].map((s, i) => (
              <div
                key={s}
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor:
                    step === s ? "#4CAF50" : i < [STEPS.EMAIL, STEPS.OTP, STEPS.NEW_PASSWORD].indexOf(step) ? "#4CAF50" : "#ddd",
                }}
              />
            ))}
          </div>

          <div className="inner-que">
            {/* ========== STEP 1: Enter Email ========== */}
            {step === STEPS.EMAIL && (
              <>
                <p>Enter your registered email address to receive a password reset OTP.</p>
                <form onSubmit={handleEmailSubmit}>
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="form-group">
                        <label htmlFor="email">
                          Email<span className="sub">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          autoComplete="off"
                          id="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                        {errorData.email ? (
                          <span className="text-danger">{errorData.email}</span>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>
                  <ul className="log-btn-main clearfix">
                    <li className="pull-left dont-have-ac">
                      <Link to="/login">Login Here</Link>
                    </li>
                    <li className="pull-right">
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Sending..." : "Send OTP"}
                      </button>
                    </li>
                  </ul>
                </form>
              </>
            )}

            {/* ========== STEP 2: Enter OTP ========== */}
            {step === STEPS.OTP && (
              <>
                <p>
                  We've sent a 6-digit OTP to <strong>{email}</strong>. Enter it below.
                </p>
                <form onSubmit={handleOtpSubmit}>
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="form-group">
                        <label>Enter OTP</label>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            justifyContent: "center",
                            marginTop: "10px",
                          }}
                          onPaste={handleOtpPaste}
                        >
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => (otpRefs.current[index] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              style={{
                                width: "48px",
                                height: "48px",
                                textAlign: "center",
                                fontSize: "20px",
                                fontWeight: "bold",
                                border: "2px solid #ddd",
                                borderRadius: "8px",
                                outline: "none",
                              }}
                              autoFocus={index === 0}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <ul className="log-btn-main clearfix">
                    <li className="pull-left dont-have-ac">
                      {countdown > 0 ? (
                        <span style={{ color: "#999" }}>Resend OTP in {countdown}s</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={handleResendOtp}
                          disabled={resending}
                          style={{ padding: 0, textDecoration: "underline" }}
                        >
                          {resending ? "Resending..." : "Resend OTP"}
                        </button>
                      )}
                    </li>
                    <li className="pull-right">
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Verifying..." : "Verify OTP"}
                      </button>
                    </li>
                  </ul>
                </form>
              </>
            )}

            {/* ========== STEP 3: New Password ========== */}
            {step === STEPS.NEW_PASSWORD && (
              <>
                <p>OTP verified! Set your new password below.</p>
                <form onSubmit={handlePasswordSubmit}>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label htmlFor="password">
                          New Password<span className="sub">*</span>
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          autoComplete="off"
                          placeholder="New Password"
                          name="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        {errorData.password ? (
                          <span className="text-danger">{errorData.password}</span>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label htmlFor="confirm_password">
                          Confirm Password<span className="sub">*</span>
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          autoComplete="off"
                          placeholder="Confirm Password"
                          name="confirm_password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        {errorData.confirm_password ? (
                          <span className="text-danger">{errorData.confirm_password}</span>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </div>

                  <ul className="log-btn-main clearfix">
                    <li className="pull-right">
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Resetting..." : "Reset Password"}
                      </button>
                    </li>
                  </ul>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
      <AuthCommonBanner></AuthCommonBanner>
    </section>
  );
};

export default ForgotPassword;
